/**
 * MarkdownTable Tool for EditorJS
 * Outputs tables as markdown for better copy-paste preservation of formatting
 */
class MarkdownTable {
  static get toolbox() {
    return {
      title: 'Markdown Table',
      icon: '<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M17 1H3C1.9 1 1 1.9 1 3V17C1 18.1 1.9 19 3 19H17C18.1 19 19 18.1 19 17V3C19 1.9 18.1 1 17 1ZM7 17H3V13H7V17ZM7 12H3V8H7V12ZM7 7H3V3H7V7ZM12 17H8V13H12V17ZM12 12H8V8H12V12ZM12 7H8V3H12V7ZM17 17H13V13H17V17ZM17 12H13V8H17V12ZM17 7H13V3H17V7Z"/></svg>'
    };
  }

  constructor({ data, config }) {
    this.data = data || {
      withHeadings: true,
      content: [
        ['Header 1', 'Header 2', 'Header 3'],
        ['Content 1-1', 'Content 1-2', 'Content 1-3'],
        ['Content 2-1', 'Content 2-2', 'Content 2-3']
      ]
    };

    this.config = config || {};
    this.element = null;
    this.wrapper = null;
  }

  static get isReadOnlySupported() {
    return true;
  }

  get CSS() {
    return {
      baseBlock: this.api.styles.block,
      wrapper: 'markdown-table-wrapper',
      table: 'markdown-table',
      row: 'markdown-table__row',
      cell: 'markdown-table__cell',
      cellHeader: 'markdown-table__cell--header',
      addButton: 'markdown-table__add-button'
    };
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add(this.CSS.baseBlock, this.CSS.wrapper);

    this.element = document.createElement('div');
    this.element.classList.add(this.CSS.table);

    this._renderTable();
    this.wrapper.appendChild(this.element);

    const addRowButton = document.createElement('button');
    addRowButton.type = 'button';
    addRowButton.innerHTML = 'Add row';
    addRowButton.classList.add(this.CSS.addButton);
    addRowButton.addEventListener('click', () => this._addRow());
    this.wrapper.appendChild(addRowButton);

    return this.wrapper;
  }

  _renderTable() {
    this.element.innerHTML = '';

    const colCount = this.data.content.length > 0
      ? this.data.content[0].length : 3;

    const table = document.createElement('table');
    table.className = 'markdown-table-real';

    if (this.data.withHeadings) {
      const thead = document.createElement('thead');
      const headerRow = document.createElement('tr');

      for (let i = 0; i < colCount; i += 1) {
        const th = document.createElement('th');
        th.contentEditable = true;
        th.innerHTML = this.data.content[0][i] || '';
        th.addEventListener('input', () => this._updateContent());
        headerRow.appendChild(th);
      }
      thead.appendChild(headerRow);
      table.appendChild(thead);
    }

    const tbody = document.createElement('tbody');
    const startRow = this.data.withHeadings ? 1 : 0;

    for (let rowIndex = startRow; rowIndex < this.data.content.length; rowIndex += 1) {
      const row = document.createElement('tr');

      for (let colIndex = 0; colIndex < colCount; colIndex += 1) {
        const td = document.createElement('td');
        td.contentEditable = true;
        td.innerHTML = this.data.content[rowIndex][colIndex] || '';
        td.dataset.row = rowIndex;
        td.dataset.col = colIndex;
        td.addEventListener('input', () => this._updateContent());
        row.appendChild(td);
      }

      if (this.data.content.length > (this.data.withHeadings ? 2 : 1)) {
        const removeCell = document.createElement('td');
        removeCell.innerHTML = '✖';
        removeCell.classList.add('remove-row-btn');
        removeCell.addEventListener('click', () => this._removeRow(rowIndex));
        row.appendChild(removeCell);
      }

      tbody.appendChild(row);
    }

    table.appendChild(tbody);

    const container = document.createElement('div');
    container.className = 'table-container';
    container.appendChild(table);

    const markdownDiv = document.createElement('div');
    markdownDiv.className = 'hidden-markdown';
    markdownDiv.innerText = this._generateMarkdown();
    container.appendChild(markdownDiv);

    this.element.appendChild(container);

    const style = document.createElement('style');
    style.innerHTML = `
      .markdown-table-wrapper {
        position: relative;
        margin-bottom: 20px;
      }
      .markdown-table-real {
        width: 100%;
        border-collapse: collapse;
        margin: 1em 0;
      }
      .markdown-table-real th, .markdown-table-real td {
        border: 1px solid #ddd;
        padding: 8px;
        min-width: 50px;
      }
      .markdown-table-real th {
        background-color: #f2f2f2;
        font-weight: bold;
        text-align: left;
      }
      .remove-row-btn {
        cursor: pointer;
        color: #ff0000;
        width: 30px;
        text-align: center;
        border: none !important;
      }
      .remove-row-btn:hover {
        background-color: #ffeeee;
      }
      .hidden-markdown {
        position: absolute;
        left: -9999px;
        top: 0;
        width: 0;
        height: 0;
        overflow: hidden;
        opacity: 0;
      }
      .markdown-table__add-button {
        margin-top: 10px;
        padding: 6px 12px;
        background: #f2f2f2;
        border: 1px solid #ddd;
        border-radius: 3px;
        cursor: pointer;
      }
      .markdown-table__add-button:hover {
        background: #e2e2e2;
      }
      .table-container {
        position: relative;
      }
      .table-container::after {
        content: 'ⓘ Tip: Select and copy table to preserve formatting';
        display: block;
        font-size: 12px;
        color: #666;
        margin-top: 5px;
        font-style: italic;
      }
      .markdown-table-real::-moz-selection,
      .markdown-table-real::selection {
        position: relative;
      }
    `;
    this.wrapper.appendChild(style);
  }

  _updateContent() {
    if (!this.element) return;

    const table = this.element.querySelector('.markdown-table-real');
    if (!table) return;

    const newContent = [];

    if (this.data.withHeadings) {
      const headerCells = Array.from(table.querySelectorAll('th'));
      if (headerCells.length > 0) {
        newContent.push(headerCells.map((cell) => cell.innerHTML));
      }
    }

    const rows = Array.from(table.querySelectorAll('tbody tr'));
    rows.forEach((row) => {
      const cells = Array.from(row.querySelectorAll('td:not(.remove-row-btn)'));
      if (cells.length > 0) {
        newContent.push(cells.map((cell) => cell.innerHTML));
      }
    });

    this.data.content = newContent;

    const markdownDiv = this.element.querySelector('.hidden-markdown');
    if (markdownDiv) {
      markdownDiv.innerText = this._generateMarkdown();
    }
  }

  _generateMarkdown() {
    if (!this.data.content || this.data.content.length === 0) {
      return '';
    }

    const colWidths = [];
    const colCount = this.data.content[0].length;

    for (let col = 0; col < colCount; col += 1) {
      colWidths.push(3);
    }

    this.data.content.forEach((row) => {
      row.forEach((cell, colIndex) => {
        if (colIndex < colCount) {
          const cellText = this._stripHtml(cell || '');
          colWidths[colIndex] = Math.max(colWidths[colIndex], cellText.length);
        }
      });
    });

    let markdown = '';

    if (this.data.withHeadings && this.data.content.length > 0) {
      markdown += '| ';
      for (let col = 0; col < colCount; col += 1) {
        const cellText = this._stripHtml(this.data.content[0][col] || '');
        markdown += `${cellText.padEnd(colWidths[col], ' ')} | `;
      }
      markdown += '\n|';

      for (let col = 0; col < colCount; col += 1) {
        markdown += `${'-'.repeat(colWidths[col] + 2)}|`;
      }
      markdown += '\n';
    }

    const startRow = this.data.withHeadings ? 1 : 0;
    for (let row = startRow; row < this.data.content.length; row += 1) {
      markdown += '| ';
      for (let col = 0; col < colCount; col += 1) {
        const cellText = this._stripHtml(this.data.content[row][col] || '');
        markdown += `${cellText.padEnd(colWidths[col], ' ')} | `;
      }
      markdown += '\n';
    }

    return markdown;
  }

  _stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  _addRow() {
    const newRow = [];
    const colCount = this.data.content.length > 0
      ? this.data.content[0].length : 3;

    for (let i = 0; i < colCount; i += 1) {
      newRow.push('');
    }

    this.data.content.push(newRow);
    this._renderTable();
  }

  _removeRow(rowIndex) {
    if (this.data.content.length <= (this.data.withHeadings ? 2 : 1)) {
      return;
    }

    this.data.content.splice(rowIndex, 1);
    this._renderTable();
  }

  save() {
    return this.data;
  }
}

export default MarkdownTable;
